
const libPictProvider = require('pict-provider');
const libLogoSlotController = require('./LogoSlotController.js');
const libLabelManagerController = require('./LabelManagerController.js');
const libLabelBehaviorController = require('./LabelBehaviorController.js');

// "My Headlight" — the configuration landing for the signed-in user's own organization (customer). First
// section is Branding: the customer's primary logo (shown across Headlight) and the secondary / reports
// logo (shared LogoSlotController, bespoke /1.0/Customer/[Secondary]Logo endpoints, raw-PNG POST). Second
// section is the customer's DEFAULT project labels (ProjectTagType rows with IDProject=0) that seed every
// new project — managed via the shared LabelManagerController. Built to grow (more preferences land below).

const CUSTOMER_LOGO_SLOTS =
[
	{ Key: 'logo', Label: 'Customer logo', Base: 'Customer/Logo', Help: 'A PNG shown across Headlight for your organization — the portal and this console. A transparent background works best.' },
	{ Key: 'secondary', Label: 'Reports logo', Base: 'Customer/SecondaryLogo', Help: 'A PNG used on generated reports and documents. Falls back to the customer logo when unset.' },
];

class MyHeadlightProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._customerName = '';
		this._loading = false;
		this._logoController = new libLogoSlotController(this.pict, 'MyHeadlight', CUSTOMER_LOGO_SLOTS);
		this._labelController = new libLabelManagerController(this.pict, 'MyHeadlight');
		// Per-observation-type label BEHAVIORS at the customer-default scope (IDProject 0, LevelContext Customer).
		this._labelBehaviorController = new libLabelBehaviorController(this.pict, 'MyHeadlight');
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	/** Role 5+ (admin) or role 4 (customer admin) editing their own customer can manage the logos. */
	get canManageLogo()
	{
		const tmpRole = Number(this.currentUser.UserRoleIndex);
		return (tmpRole >= 5) || (tmpRole === 4);
	}

	/** Role 4+ (customer admin) can manage their own organization's default project labels. */
	get canManageLabels()
	{
		return Number(this.currentUser.UserRoleIndex) >= 4;
	}

	_getJSON(pURL)
	{
		return new Promise((resolve) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => resolve(pError ? null : pBody));
		});
	}

	// --- Loading ---

	async loadAsync()
	{
		this._loading = true;
		this._render();

		const tmpCustomer = await this._getJSON(`Customer/${this.idCustomer}`);
		this._customerName = (tmpCustomer && tmpCustomer.Name) ? tmpCustomer.Name : `Customer ${this.idCustomer}`;
		this._logoController.setContext(this.idCustomer, this.canManageLogo, () => this._render());
		this._labelController.setContext({ IDProject: 0, IDCustomer: this.idCustomer }, this.canManageLabels, () => this._render(), false);
		this._labelBehaviorController.setContext({ IDProject: 0, IDCustomer: this.idCustomer }, this.canManageLabels, () => this._syncLabelBehavior());
		this._loading = false;
		this._render();
		// Fill the logos + default labels in the background; each repaints when its data is ready.
		this._logoController.probeAll().then(() => this._render());
		this._labelController.load();
		this._labelBehaviorController.load();
	}

	// --- Logo slot delegates (the shared Headlight-LogoSlot template calls these by provider name) ---

	uploadSlot(pKey, pInput)
	{
		return this._logoController.upload(pKey, pInput);
	}

	removeSlot(pKey)
	{
		return this._logoController.remove(pKey);
	}

	// --- Default-label delegates (the shared Headlight-LabelManager template calls these by provider name) ---

	labelAdd() { return this._labelController.add(); }
	labelUpdateName(pID, pValue) { return this._labelController.updateName(pID, pValue); }
	labelRemove(pID) { return this._labelController.remove(pID); }
	labelSetNew(pField, pValue) { return this._labelController.setNew(pField, pValue); }
	labelCopyDefaults() { return this._labelController.copyDefaults(); }

	// --- Label-behavior delegates (the shared Headlight-LabelBehavior template calls these by provider name) ---

	labelBehaviorSelectType(pType) { return this._labelBehaviorController.selectType(pType); }
	labelBehaviorSetFreeEntry(pTagType, pDeny) { return this._labelBehaviorController.setFreeEntry(pTagType, pDeny); }
	labelBehaviorSetMaxCount(pTagType, pValue) { return this._labelBehaviorController.setMaxCount(pTagType, pValue); }
	labelBehaviorSetHidden(pTagType, pHidden) { return this._labelBehaviorController.setHidden(pTagType, pHidden); }
	labelBehaviorSetDistilling(pEnabled) { return this._labelBehaviorController.setDistilling(pEnabled); }

	/** Repaint ONLY the label-behavior host (keeps the type picker + scroll) — full re-render as fallback. */
	_syncLabelBehavior()
	{
		this.deriveRenderState();
		if (this.pict.ContentAssignment.getElement('#MH-LabelBehaviorHost').length > 0)
		{
			const tmpSlot = (this.viewState.LabelBehaviorStateSlot || [])[0];
			if (tmpSlot)
			{
				this.pict.ContentAssignment.assignContent('#MH-LabelBehaviorHost', this.pict.parseTemplateByHash('Headlight-LabelBehavior', tmpSlot));
				return;
			}
		}
		this._render();
	}

	// --- Render-state ---

	get viewState()
	{
		if (!this.pict.AppData.MyHeadlight)
		{
			this.pict.AppData.MyHeadlight =
			{
				LoadingSlot: [], BodySlot: [],
				CustomerName: '', CustomerLine: '', LogoSlots: [], LabelStateSlot: [], LabelBehaviorStateSlot: [],
			};
		}
		return this.pict.AppData.MyHeadlight;
	}

	deriveRenderState()
	{
		const tmpState = this.viewState;
		if (this._loading)
		{
			tmpState.LoadingSlot = [ {} ];
			tmpState.BodySlot = [];
			return;
		}
		tmpState.LoadingSlot = [];
		tmpState.BodySlot = [ {} ];
		tmpState.CustomerName = this._customerName;
		tmpState.CustomerLine = `${this._customerName} · Customer #${this.idCustomer}`;
		tmpState.LogoSlots = this._logoController.buildRecords();
		tmpState.LabelStateSlot = [ this._labelController.buildState() ];
		tmpState.LabelBehaviorStateSlot = [ this._labelBehaviorController.buildState() ];
	}

	_render()
	{
		this.deriveRenderState();
		if (this.pict.views.MyHeadlight)
		{
			this.pict.views.MyHeadlight.render();
		}
	}
}

module.exports = MyHeadlightProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'MyHeadlight',
	AutoInitialize: true,
	AutoInitializeOrdinal: 222,
};
